import React, { useState, useEffect } from 'react'
import {
  Container,
  TextTitle,
  ListContainer,
  TextBody,
  Image,
  FieldText,
  FieldFile,
  SelectAutocomplete,
  BlockingLoader
} from '@pmi/pmi-iqos-ds-react'
import {
  Assets,
  registerForm,
  unregisterForm,
  validateForm,
  registerParam,
  unegisterParam,
  validateParam,
  constants,
  htmlTranslation
} from '@pmi/pmi-iqos-shared-react'
import { DcxApiManager, ZynAnalytics } from '@zyn-common/services'

const CheckoutTVA = ({
  savingTvaData,
  translations,
  fileNameFront,
  fileNameBack,
  uploadPhoto,
  updatedTvaData,
  handleChangeInput,
  handleChangeZipCode,
  uploadingFile,
  setFormIsValidTVA,
  zenrinErrors,
  line,
  deliveryAddressFormSection
}) => {
  let fileInputElFront = null
  const triggerInputFileFront = () => fileInputElFront.click()
  let fileInputElBack = null
  const triggerInputFileBack = () => fileInputElBack.click()

  const [postalCodeErrors, setPostalCodeErrors] = useState([])
  const [prefectureErrors, setPrefectureErrors] = useState([])
  const [cityErrors, setCityErrors] = useState([])
  const [addressErrors, setAddressErrors] = useState([])

  const api = new DcxApiManager(line)

  const formFieldNames = {
    prefecture: 'prefecture',
    city: 'city',
    address: 'address',
    postalCode: 'postal_code'
  }

  const formId = registerForm()

  const validators = {}

  const updateValidators = () => {
    validators[formFieldNames.postalCode] = registerParam(
      [
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.postalCode],
          description: translations.tk_zenrinValidation
        }
      ],
      false,
      formId,
      updatedTvaData.postal_code,
      errors => {
        setPostalCodeErrors(errors)
      }
    )
    validators[formFieldNames.prefecture] = registerParam(
      [
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.prefecture],
          description: translations.tk_zenrinValidation
        }
      ],
      false,
      formId,
      updatedTvaData.prefecture,
      errors => {
        setPrefectureErrors(errors)
      }
    )
    validators[formFieldNames.city] = registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_addressCharactersValidation
        },
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.city],
          description: translations.tk_zenrinValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_cityAddressInvalidComma
        }
      ],
      false,
      formId,
      updatedTvaData.city,
      errors => {
        setCityErrors(errors)
      }
    )
    validators[formFieldNames.address] = registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_addressCharactersValidation
        },
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.address],
          description: translations.tk_zenrinValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_buildingNameInvalidComma
        }
      ],
      true,
      formId,
      updatedTvaData.address,
      errors => {
        setAddressErrors(errors)
      }
    )
  }

  updateValidators()

  useEffect(() => {
    setPostalCodeErrors(
      validateParam(
        updatedTvaData.postal_code,
        validators[formFieldNames.postalCode],
        formId
      )
    )
    setPrefectureErrors(
      validateParam(
        updatedTvaData.prefecture,
        validators[formFieldNames.prefecture],
        formId
      )
    )
    setCityErrors(
      validateParam(
        updatedTvaData.city,
        validators[formFieldNames.city],
        formId
      )
    )
    setAddressErrors(
      validateParam(
        updatedTvaData.address,
        validators[formFieldNames.address],
        formId
      )
    )
    setFormIsValidTVA(validateForm(formId))
  }, [updatedTvaData])

  useEffect(() => {
    updateValidators()
    setFormIsValidTVA(validateForm(formId))
  }, [zenrinErrors])

  useEffect(() => {
    return () => {
      unregisterForm(formId)
      Object.keys(formFieldNames).forEach(fieldNameKey =>
        unegisterParam(validators[formFieldNames[fieldNameKey]])
      )
    }
  }, [])

  const promiseOptions = zipcode =>
    zipcode && zipcode.length > 2
      ? api.auth
          .getAddressInfo({
            params: {
              zipcode
            }
          })
          .then(res => {
            const results = res.zip_codes.map(item => {
              return {
                value: item.zip_code,
                label:
                  item.zip_code +
                  ' ' +
                  item.state +
                  ' ' +
                  item.city +
                  ' ' +
                  item.village,
                item: item
              }
            })
            return results
          })
      : Promise.resolve([])

  const handleUploadImage = (e, type) => {
    uploadPhoto(e.target.files[0], type)
    ZynAnalytics.checkoutTvaClickUpload(false, type)
  }

  return (
    <Container id={deliveryAddressFormSection}>
      <BlockingLoader loading={uploadingFile} />
      <Container>
        <Container pb={['6', '8']}>
          <TextTitle heading={TextTitle.headings.h4}>
            {translations.tk_sectionTitle}
          </TextTitle>
        </Container>
        <Container pb={'8'}>
          <TextBody {...htmlTranslation(translations, 'tk_textTop')} />
        </Container>
        <Container>
          <ListContainer
            columns={[1, 2]}
            xBetween="4"
            yBetween="2"
            overflow={ListContainer.overflows.unset}>
            <Container pt="4" fullWidth>
              <SelectAutocomplete
                name={formFieldNames.postalCode}
                label={translations.tk_postalCode}
                placeholder={translations.tk_postalCode}
                value={updatedTvaData.postal_code}
                onChangeHandler={handleChangeZipCode}
                loadOptions={promiseOptions}
                errors={postalCodeErrors}
                disabled={savingTvaData}
                data-testing="Profile_SelectAutocomplete_PostalCode"
              />
            </Container>
            <Container pt="4" fullWidth>
              <FieldText
                name={formFieldNames.prefecture}
                label={translations.tk_prefecture}
                value={updatedTvaData.prefecture}
                onChangeHandler={() => {}}
                disabled
                errors={prefectureErrors}
                data-testing="Profile_FieldText_Prefecture"
              />
            </Container>
            <Container pt="4" fullWidth>
              <FieldText
                name={formFieldNames.city}
                label={translations.tk_city}
                value={updatedTvaData.city}
                onChangeHandler={handleChangeInput}
                errors={cityErrors}
                disabled={savingTvaData}
                data-testing="Profile_FieldText_City"
              />
            </Container>
            <Container pt="4" fullWidth>
              <FieldText
                name={formFieldNames.address}
                label={translations.tk_street}
                value={updatedTvaData.address}
                onChangeHandler={handleChangeInput}
                errors={addressErrors}
                disabled={savingTvaData}
                data-testing="Profile_FieldText_Address"
              />
            </Container>
          </ListContainer>
        </Container>
        <Container pt={['8', '10']} display={Container.displays.block}>
          <Container pb="8">
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_textMiddleTitle}
            </TextTitle>
          </Container>
          <TextBody {...htmlTranslation(translations, 'tk_textMiddle')} />
          <Container
            pt="10"
            flexWrap
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}>
            <Container>
              <input
                ref={fileInput => (fileInputElFront = fileInput)}
                type="file"
                disabled={savingTvaData}
                onClick={e => (e.target.value = null)}
                onChange={e => handleUploadImage(e, 'front')}
                style={{ display: 'none' }}
                data-testing="Profile_File_TVAFront"
              />
              <input
                ref={fileInput => (fileInputElBack = fileInput)}
                type="file"
                disabled={savingTvaData}
                onClick={e => (e.target.value = null)}
                onChange={e => handleUploadImage(e, 'back')}
                style={{ display: 'none' }}
                data-testing="Profile_File_TVABack"
              />
            </Container>
            <Container
              fullWidth
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}
              pb="10">
              <Image
                src={Assets.urls.imageUploadCertificate}
                width={['full', '1/2']}
                alt="registration-document"
              />
            </Container>
            <Container
              pb={['10', '6']}
              flexWrap
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}
              divider={Container.dividers.bottom}>
              <Container pb="4" width={['10/12', '6/12', '6/12']}>
                <FieldFile
                  fullWidth
                  label={translations.tk_uploadButtonFront}
                  value={fileNameFront}
                  onClickHandler={triggerInputFileFront}
                  data-testing="Profile_FieldFile_TVAFront"
                />
              </Container>
              <Container
                pl={['0', '4', '4']}
                pb="4"
                width={['10/12', '6/12', '6/12']}>
                <FieldFile
                  fullWidth
                  label={translations.tk_uploadButtonBack}
                  value={fileNameBack}
                  onClickHandler={triggerInputFileBack}
                  data-testing="Profile_FieldFile_TVABack"
                />
              </Container>
            </Container>
          </Container>
        </Container>
      </Container>
    </Container>
  )
}

export default CheckoutTVA
